import { TableFooter, TableSortLabel } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import styles from './table.module.scss';

import _ from 'lodash';

import {
  IconWrapper,
  ellipsizeMiddle,
  formatHumanDate,
  formatToMaxFourDigits,
} from '@flexo/general';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';

import { KPI, RIGHT_DRAWER, store } from '@flexo/reducers';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { TableSkeleton } from '../skeletons/table-skeleton';
import { openPdfWithTitle } from './helpers';
import { useDrawerTableSpacer } from './use-drawer-table-spacer';

export function InvoicesPeriodDetailsTable(props: any) {
  const { t } = useTranslation();
  const dispatch = store.dispatch;
  const metadata = useSelector((state: any) => state.metadata);
  const rightDrawer = useSelector((state: any) => state.rightDrawer);
  const { no_data_text, rows } = t(
    `widgets.tables.InvoicesPeriodDetailsTable`,
    {
      returnObjects: true,
    }
  ) as any;

  const title = t('widgets.tables.InvoicesPeriodDetailsTable.title', {
    communityName: metadata.selectedEntity?.communityName,
    period: props.data.data?.period,
  });
  const subtitle = t('widgets.tables.InvoicesPeriodDetailsTable.subtitle', {
    date: formatHumanDate(props.data.data?.sites[0]?.emission_date),
  });
  const columns = rows;

  const [storedRows, setStoredRows] = useState<Array<any>>([]);
  const [rowsOrder, setRowsOrder] = useState(['invoice', 'asc']);
  const [loadingInvoice, setLoadingInvoice] = useState<string | null>(null);
  const { bodyRef, containerRef, footerRef, headRef, spacerHeight } =
    useDrawerTableSpacer(storedRows.length > 0);

  const handleDownloadInvoice = async (row: any) => {
    // If file_base64 is already available (legacy), use it directly
    if (row?.file_base64) {
      openPdfWithTitle(
        row.file_base64,
        row?.invoiceID || row?.fileName || 'Document'
      );
      return;
    }

    // Otherwise, fetch on-demand
    setLoadingInvoice(row?.invoice_no || row?.invoiceID);
    try {
      const result: any = await dispatch(
        KPI.FetchKwickInvoiceFileAsync({
          invoiceNo: row?.invoice_no || row?.invoiceID,
          siteId: row?.siteId,
        }) as any
      );
      if (result.payload?.file_base64) {
        openPdfWithTitle(
          result.payload.file_base64,
          row?.invoiceID || row?.fileName || 'Document'
        );
      }
    } finally {
      setLoadingInvoice(null);
    }
  };

  useEffect(() => {
    const _data = props.data.data?.sites.map((site: any) => {
      const member = metadata?.entities?.members?.find(
        (member: any) => member.memberId === (site.userId || site.memberId)
      );
      const memberName = [member?.memberFirstName, member?.memberLastName]
        .filter(Boolean)
        .join(' ');
      const siteId = site?.siteId || site?.siteID || site?.fk_site || null;
      const memberId =
        site?.userId || site?.memberId || member?.memberId || null;

      return {
        ...site,
        siteId,
        memberId,
        memberName,
        memberExternalId: member?.memberExternalId || null,
        siteType: site?.siteType?.[0]?.siteType,
      };
    });

    setStoredRows(_data || []);
  }, [props.data, metadata?.entities?.members]);

  function sortRows(columnId: string) {
    try {
      let _midArray = _.cloneDeep(storedRows);
      let direction = 'asc';

      if (rowsOrder[0] === columnId && rowsOrder[1] === 'asc') {
        direction = 'desc';
      }

      switch (columnId) {
        case 'invoice':
          _midArray = _midArray.sort((a, b) =>
            direction === 'asc'
              ? a.invoice_no.localeCompare(b.invoice_no)
              : b.invoice_no.localeCompare(a.invoice_no)
          );
          break;
        case 'amount':
          _midArray = _midArray.sort((a, b) =>
            (direction === 'asc' ? a.amount > b.amount : a.amount < b.amount)
              ? 1
              : -1
          );
          break;
        case 'member':
          _midArray = _midArray.sort((a, b) =>
            direction === 'asc'
              ? a.memberName.localeCompare(b.memberName)
              : b.memberName.localeCompare(a.memberName)
          );
          break;
        case 'site':
          _midArray = _midArray.sort((a, b) =>
            direction === 'asc'
              ? a.siteName.localeCompare(b.siteName)
              : b.siteName.localeCompare(a.siteName)
          );
          break;
      }

      setRowsOrder([columnId, direction]);
      setStoredRows(_midArray);
    } catch (err: any) {
      // console.warn(err)
    }
  }

  return (
    <div className={styles.Table__DrawerViewport}>
      {/* {LOADING_STATE === 'PENDING' && <Shimmer />} */}
      {storedRows?.length > 0 ? (
        <div className={styles.Table__InvoicePeriodDetails__OverflowContainer}>
          <div
            className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__InvoicePeriodDetails}`}
          >
            <div
              className={`${styles.Table__InvoicePeriodDetails__TitleContainer}`}
            >
              <div className={`${styles.Table__InvoicePeriodDetails__Title}`}>
                {parse(title)}
              </div>
              <div
                className={`${styles.Table__InvoicePeriodDetails__Subtitle}`}
              >
                {subtitle}
              </div>
              <div
                className={`${styles.Table__InvoicePeriodDetails__Close}`}
                onClick={() => {
                  dispatch(RIGHT_DRAWER.ResetDrawer());
                }}
              >
                <IconWrapper iconName="cross" />
              </div>
            </div>

            <TableContainer
              component={Paper}
              className={styles.Table__InvoicePeriodDetails__TableContainer}
              ref={containerRef}
            >
              <Table aria-label="simple table">
                <TableHead
                  className={styles.Table__InvoicePeriodDetails__Head}
                  ref={headRef}
                >
                  <TableRow>
                    {columns &&
                      columns.map((column: any, column_i: number) => (
                        <TableCell
                          className={`
                ${
                  column.sortable
                    ? styles.Table__Header__Cell__Sortable
                    : styles.Table__Header__Cell__Static
                }
              ${
                column.sortable && rowsOrder[0] === column.field
                  ? styles.Table__Header__Cell__Active
                  : styles.Table__Header__Cell
              }`}
                          key={`invoice_period_detail_table_${column_i}`}
                        >
                          <TableSortLabel
                            active={
                              column.sortable && rowsOrder[0] === column.field
                            }
                            direction={
                              rowsOrder[0] === column.field &&
                              rowsOrder[1] === 'asc'
                                ? 'asc'
                                : 'desc'
                            }
                            onClick={() =>
                              column.sortable ? sortRows(column.field) : null
                            }
                            className={`${styles.Table__Header__Cell}`}
                            IconComponent={() =>
                              rowsOrder[0] === column.field ? (
                                rowsOrder[1] === 'asc' ? (
                                  <IconWrapper
                                    iconName="chevron-up"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                ) : (
                                  <IconWrapper
                                    iconName="chevron-down"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                )
                              ) : (
                                <div style={{ height: 32, width: 19 }}></div>
                              )
                            }
                          >
                            <div> {column.title}</div>
                          </TableSortLabel>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody ref={bodyRef}>
                  {(storedRows || []).map((row) => (
                    <TableRow
                      key={row?.invoice_no || row?.invoiceID || row?.siteId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      style={{ height: 100 }}
                      data-table-body-row="true"
                      className={``}
                    >
                      <TableCell
                        className={`${
                          !columns?.[0]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoice_period_detail_table_row_${row.siteId}_cell_0`}
                        component="th"
                        scope="row"
                      >
                        <div
                          className={`${styles.Table__Cell__InvoicePeriodDetails__InvoiceNO}`}
                        >
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadInvoice(row);
                            }}
                            style={{
                              pointerEvents: loadingInvoice ? 'none' : 'auto',
                              opacity:
                                loadingInvoice ===
                                (row?.invoice_no || row?.invoiceID)
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            {row?.invoice_type &&
                              t(
                                `widgets.tables.paymentsTable.invoiceType.${row?.invoice_type}`
                              )}{' '}
                            {row.invoiceID}
                            {loadingInvoice ===
                            (row?.invoice_no || row?.invoiceID) ? (
                              <CircularProgress
                                size={16}
                                color="inherit"
                                sx={{
                                  marginLeft: '4px',
                                  marginTop: '-6px',
                                  transformOrigin: 'center',
                                  animation: 'none',
                                }}
                              />
                            ) : (
                              <IconWrapper iconName="download" />
                            )}
                          </a>
                        </div>
                      </TableCell>

                      <TableCell
                        key={`invoice_period_detail_table_row_${row.siteId}_cell_1`}
                        component="th"
                        scope="row"
                      >
                        <div
                          className={`${
                            !columns?.[0]?.sortable === true &&
                            styles.Table__Cell__Static
                          }`}
                        >
                          <p
                            className={`${styles.Table__Cell__InvoicePeriodDetails__SiteName}`}
                            onClick={() => {
                              if (!row?.siteId) {
                                return;
                              }
                              dispatch(RIGHT_DRAWER.ResetDrawer());
                              navigateToSite('site', row?.siteId);
                            }}
                          >
                            {ellipsizeMiddle(row?.siteName || row?.siteType)}
                          </p>
                          <p
                            className={`${styles.Table__Cell__InvoicePeriodDetails__SiteID}`}
                          >
                            {ellipsizeMiddle(row?.pod || row?.siteExternalId)}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !columns?.[1]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoice_period_detail_table_row_${row.siteId}_cell_2`}
                        component="th"
                        scope="row"
                      >
                        <div
                          className={`${styles.Table__Cell__InvoicePeriodDetails__Member}`}
                        >
                          <p
                            className={`${styles.Table__Cell__InvoicePeriodDetails__MemberName}`}
                            onClick={() => {
                              if (!row?.memberId) {
                                return;
                              }
                              dispatch(RIGHT_DRAWER.ResetDrawer());
                              navigateToMember('member', row?.memberId);
                            }}
                          >
                            {ellipsizeMiddle(row?.memberName)}
                          </p>
                          <p
                            className={`${styles.Table__Cell__InvoicePeriodDetails__MemberID}`}
                          >
                            {ellipsizeMiddle(
                              row?.memberExternalId || row?.memberId
                            )}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !columns?.[3]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoice_period_detail_table_row_${row.siteId}_cell_4`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <div
                            style={{
                              width: '100px',
                            }}
                          >
                            <p
                              className={`${styles.Table__Cell__InvoicePeriodDetails__Amount}`}
                            >
                              {props.data.data?.sites?.[0]?.unit
                                ? ` ${props.data.data.sites[0].unit}`
                                : ''}{' '}
                              {formatToMaxFourDigits(row?.amount, true)}
                              <IconWrapper
                                style={{
                                  transform: row?.paid
                                    ? 'scale(0.6)'
                                    : 'scale(1)',
                                  marginRight: row?.paid ? '-5px' : '0px',
                                }}
                                iconName={`${
                                  row?.paid
                                    ? 'tick_-encircled_active_secondary'
                                    : 'not_paid'
                                }`}
                              />
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {spacerHeight > 0 && (
                    <TableRow className={styles.Table__DrawerSpacerRow}>
                      <TableCell colSpan={columns.length}>
                        <div
                          className={styles.Table__DrawerSpacer}
                          style={{ height: spacerHeight }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                <TableFooter
                  className={styles.Table__InvoicePeriodDetails__Footer}
                  ref={footerRef}
                >
                  <TableRow>
                    <TableCell
                      align="left"
                      className={`${
                        columns[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          columns[0].sortable ? sortRows('invoice') : null
                        }
                        className={`${
                          columns[0].sortable && rowsOrder[0] === 'invoice'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        {storedRows.length}{' '}
                        {storedRows.length === 1
                          ? t('widgets.tables.utils.total_period')
                          : t('widgets.tables.utils.total_periods')}
                        {rowsOrder[0] === 'invoice' ? (
                          rowsOrder[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        columns[1].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      {/* <div
                      onClick={() =>
                        columns[1].sortable ? sortRows('amount') : null
                      }
                      className={`${
                        columns[1].sortable && rowsOrder[0] === 'amount'
                          ? styles.Table__Footer__Cell__Active
                          : styles.Table__Footer__Cell
                      }`}
                        >

                      {rowsOrder[0] === 'amount' ? (
                        rowsOrder[1] === 'asc' ? (
                          <IconWrapper
                            iconName="chevron-up"
                            className={`${styles.Table__TH__Icon}`}
                          />
                        ) : (
                          <IconWrapper
                            iconName="chevron-down"
                            className={`${styles.Table__TH__Icon}`}
                          />
                        )
                      ) : (
                        <div style={{ height: 32, width: 19 }}></div>
                      )}
                    </div> */}
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        columns[2].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    ></TableCell>
                    <TableCell
                      align="left"
                      style={{
                        paddingTop: 0,
                      }}
                    >
                      <div
                        onClick={() =>
                          columns[2].sortable ? sortRows('amount') : null
                        }
                        className={`${
                          columns[2].sortable && rowsOrder[0] === 'amount'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                        style={{
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        {props.data.data?.sites?.[0]?.unit
                          ? ` ${props.data.data.sites[0].unit} `
                          : ''}

                        {props.data.data?.amount !== props.data.data?.received
                          ? `${formatToMaxFourDigits(
                              props.data.data?.received,
                              true
                            )} ${t(
                              'widgets.tables.utils.paid'
                            )} / ${formatToMaxFourDigits(
                              props.data.data?.amount,
                              true
                            )} ${t('widgets.tables.utils.total')}`
                          : `${formatToMaxFourDigits(
                              props.data.data?.amount,
                              true
                            )} ${t('widgets.tables.utils.total')}`}

                        {rowsOrder[0] === 'amount' ? (
                          rowsOrder[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </div>
        </div>
      ) : (
        <div
          className={`${styles.Table} mb-3`}
          key={'invoices_period_details_table_skeleton_wrapper'}
        >
          <div className={`${styles.Table__Title}`}>{title}</div>
          <TableSkeleton rows={rows} context="communities" hasShimmer={false} />
        </div>
      )}
    </div>
  );
}

export default InvoicesPeriodDetailsTable;
